import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, DialogActions, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState, useRecoilValue } from 'recoil';
import { MerSETASDIService } from '../../../../../Component/Api/merSETA/Internal/MerSETASDIService';
import { MerSETALAIService } from '../../../../../Component/Api/merSETA/LAI/MerSETALAIService';
import AutocompleteWithInfo, { IDropDownOptionsWithDescription } from '../../../../../Component/Common/Components/Autocomplete/AutocompleteWithInfo';
import { AppDatepicker } from '../../../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import { IDropDownOptions } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { IntermediateGlobalLoading } from '../../../../../Component/Common/Components/Loading/IntermediateGlobalLoading';
import { MDButton } from '../../../../../Component/Common/Components/MDButton';
import { IDropDownTabOptions } from '../../../../../Component/Common/Components/OptionsSelectDropdown/OptionsSelectDropdown';
import { BEECatergoryDto } from '../../../../../Component/Model/Dto/WSP/MerSETASDE';
import { MerSetaSdi } from '../../../../../Component/Model/Dto/WSP/MerSETASDI';
import { HttpStatusCode } from '../../../../../Component/Model/Enum/HttpStatusCode';
import { getIOptionsArray, getIOptionsArrayWithInfo } from '../../../../../Helpers/DropdownWithDescriptionHelper';
import { OrganisationAtom } from '../../../../../State/Organisation.Atom';
import { CreateMerSetaSdiValidationSchema } from './CreateMerSetaSdiValidationSchema';
import { InterventionAtom } from '../Intervention.Atom';

export const InterventionDetails = () => {
    const history = useHistory();
    const { control, handleSubmit, watch, reset, formState: { isSubmitting }, getValues, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(CreateMerSetaSdiValidationSchema)
    });

    const [selectedSDI, setSelectedSDI] = useRecoilState(InterventionAtom);

    const peopleAndTrainingOrganisation = useRecoilValue(OrganisationAtom);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
    const [skillsSetCodes, setSkillsSetCodes] = useState<IDropDownOptionsWithDescription[]>([]);
    const [skillsProgrammeCodes, setSkillsProgrammeCodes] = useState<IDropDownOptionsWithDescription[]>([]);
    const [beeCategoryCodes, setBeeCategoryCodes] = useState<IDropDownOptions[]>([]);
    const [interventionTitles, setInterventionTitles] = useState<IDropDownOptions[]>([]);
    const [beeCategories, setBeeCategories] = useState<BEECatergoryDto[]>([]);
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();

    const selectedBeeCategory = watch('beeCategoryId');
    const selectedInterventionType = watch('type');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true)
        try {
            const skillSetCodesResultPromise = MerSETALAIService.GetMerSETASkillsSetCodes();
            const skillProgrammeCodesResultPromise = MerSETALAIService.GetMerSETASkillsProgrammeCodes();
            const interventionTitlesResultPromise = MerSETALAIService.GetMerSETAInterventionTitles();
            const beeCategoryResultPromise = MerSETASDIService.GetMerSETACategoryInterventions();

            const [skillSetCodesResult, skillProgrammeCodesResult, interventionTitlesResult, beeCategoryResult] = await Promise.all([
                skillSetCodesResultPromise,
                skillProgrammeCodesResultPromise,
                interventionTitlesResultPromise,
                beeCategoryResultPromise
            ]);



            const skillsSetOptions = getIOptionsArrayWithInfo(
                skillSetCodesResult.data,
                'id',
                'programmeId',
                'description'
            )
            const skillsProgrammeOptions = getIOptionsArrayWithInfo(
                skillProgrammeCodesResult.data,
                'id',
                'programmeId',
                'description'
            )
            const interventionTitleOptions = getIOptionsArray(
                interventionTitlesResult.data,
                'code',
                'description'
            )

            const beeCategoryCodeOptions = getIOptionsArray(
                beeCategoryResult.data,
                'id',
                'category'
            )

            setBeeCategories(beeCategoryResult.data);
            setSkillsSetCodes(skillsSetOptions);
            setSkillsProgrammeCodes(skillsProgrammeOptions);
            setInterventionTitles(interventionTitleOptions);
            setBeeCategoryCodes(beeCategoryCodeOptions);


        }
        catch (error) {
            console.log(error);
        }
        finally {
            setIsLoading(false);

        }
    }


    const handleDataSubmit = async (data: FieldValues) => {

        let request: MerSetaSdi = {
            organisationId: +peopleAndTrainingOrganisation.OrganisationId,
            identifier: data.identifier,
            merSetaInterventionTypesLookupId: data.type.id,
            merSetaInterventionTitlesLookupId: data.title.id,
            courseName: data.courseName,
            merSetaBEECategoryId: data.beeCategoryId.id,
            startDate: data.startDate,
            endDate: data.endDate,
            merSetaSkillsSetCodeLookupId: data.skillsSet != null ? data.skillsSet.id : undefined,
            merSetaSkillsProgrammeCodesLookupId: data.skillsProgramme != null ? data.skillsProgramme.id : undefined,
        };

        if (selectedSDI.id > 0) {
            request = {
                ...request,
                id: selectedSDI.id
            }
        }

        try {
            setIsLoading(true);
            if (selectedSDI.id > 0) {
                const result = await MerSETASDIService.UpdateMerSETASDI(selectedSDI.id, request);
                if (result.status === HttpStatusCode.OK) {
                    toast.success("Entry updated successfully");
                }
            } else {
                const result = await MerSETASDIService.CreateMerSETASDI(request);
                if (result.status === HttpStatusCode.OK) {
                    toast.success("Entry saved successfully");
                    setSelectedSDI({
                        id: result.data
                    });
                }
            }
        }
        catch (error) {
            console.error(error);
            toast.error(isEditing ? "Failed to update entry" : "Failed to create entry");
        }
        finally {
            setIsLoading(false);
        }
    }


    let interventionTypes: IDropDownTabOptions[] = getIOptionsArray(
        beeCategories?.find(category => category.id === selectedBeeCategory?.id)?.interventionTypes ?? [],
        'code',
        'description'
    );


    useEffect(() => {
        if (selectedSDI.id > 0 && beeCategoryCodes.length > 0) {
            getMersetaRecord();
        }

    }, [selectedSDI.id, beeCategoryCodes])

    const getMersetaRecord = async () => {
        setIsEditing(true);
        setIsLoadingForm(true);
        try {
            const result = await MerSETASDIService.GetById(selectedSDI.id!);
            reset(result.data);
            const selectedData = beeCategoryCodes.find(x => x.id === result.data.merSetaBEECategoryId);
            interventionTypes = getIOptionsArray(
                beeCategories?.find(category => category.id === selectedData?.id)?.interventionTypes ?? [],
                'code',
                'description'
            );
            setValue('beeCategoryId', selectedData);
            const selectedType = interventionTypes.find(x => x.id === result.data.merSetaInterventionTypesLookupId);
            const selectedTitle = interventionTitles.find(x => x.id === result.data.merSetaInterventionTitlesLookupId);
            const selectedSkillSet = skillsSetCodes.find(x => x.id === result.data.merSetaSkillsSetCodeLookupId);
            const selectedProgramme = skillsProgrammeCodes.find(x => x.id === result.data.merSetaSkillsSetCodeLookupId);
            setValue('type', selectedType);
            setValue('title', selectedTitle);
            setValue('skillsSet', selectedSkillSet)
            setValue('skillsProgramme', selectedProgramme)
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingForm(false);
        }
    }

    if ((interventionTypes?.find(x => x.id === selectedInterventionType?.id) === undefined) && getValues('type') !== null) {
        setValue('type', null)
    }

    if (isLoadingForm || isLoading) return (
        <IntermediateGlobalLoading />
    )
    return (
        <Box>
            <Grid container spacing={3} alignItems="center" onSubmit={handleSubmit(handleDataSubmit)} id='addSdeForm' component={'form'}>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="beeCategoryId"
                        label="BEE Category *"
                        items={beeCategoryCodes}
                        size={"small"}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="type"
                        label="Intervention Type *"
                        items={interventionTypes}
                        disabled={interventionTypes.length === 0}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="title"
                        label="Intervention Title *"
                        items={interventionTitles}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppTextInput
                        label='Course Name *'
                        name='courseName'
                        control={control}
                    />
                </Grid>

                <Grid item xs={6} sm={6}>
                    <AppDatepicker
                        control={control}
                        name="startDate"
                        label="Start Date *"
                        inputFormat='yyyy/MM/dd'
                        maxDate={endDate}
                        onChange={setStartDate}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppDatepicker
                        control={control}
                        name="endDate"
                        label="End Date *"
                        inputFormat='yyyy/MM/dd'
                        minDate={startDate}
                        onChange={setEndDate}
                    />
                </Grid>

                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="skillsSet"
                        label="Skills Set"
                        items={skillsSetCodes}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="skillsProgramme"
                        label="Skills Programme"
                        items={skillsProgrammeCodes}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppTextInput
                        label='Intervention Identifier *'
                        name='identifier'
                        control={control}
                    />
                </Grid>
            </Grid>
            <DialogActions>
                <LoadingButton loading={isSubmitting} variant='contained' form='addSdeForm' type="submit" color='primary'>Save</LoadingButton>
            </DialogActions>
        </Box>
    )
}

